import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useTheme } from '@mui/material'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { Trash, CloseCircle } from 'iconsax-react'

import { ObjectType } from '../../../types'

import Button from '@SonarCX/components/atoms/Button2'
import FlowFieldOption from '../FlowFieldOption'

import { cleanFlow } from '../Flow/helpers'
import { updateFlow } from '@SonarCX/slices/customFields/flows'
import useStyles from './styles'

type FlowFieldOptionsProps = {
  flow: APIFlow
  field: LocalCustomField
  tab: ObjectType
}

const FlowFieldOptions: React.FC<FlowFieldOptionsProps> = ({
  flow,
  field,
  tab
}) => {
  const theme = useTheme()

  const dispatch = useAppDispatch()

  const [newOption, setNewOption] = React.useState<APIFlowFieldOption>(null)

  const fieldType = React.useMemo(() => field.field_type, [field])

  const [options, setOptions] = React.useState<APIFlowFieldOption[]>([])

  // Sync options with flow
  React.useEffect(() => {
    const flowField = flow.fields.find(flowField => flowField.id === field.id)
    setOptions(flowField?.options ?? [])
  }, [flow, field])

  const optionsRef = React.useRef<APIFlowFieldOption[]>([])

  React.useEffect(() => {
    optionsRef.current = cloneDeep(options)
  }, [options])

  const timeoutRef = React.useRef<any>(null)

  const canConfirm = React.useMemo(() => {
    if (newOption === null) {
      return false
    }

    let val: any = newOption.when ?? ''
    if (val instanceof Date) {
      val = val.toISOString()
    }

    return val.trim().length > 0 && newOption.then !== null
  }, [newOption])

  const classes = useStyles()

  const addNewOption = React.useCallback(() => {
    setNewOption({ when: '', then: null })
  }, [])

  const confirmNewOption = React.useCallback(() => {
    const newFlow = cleanFlow(flow)
    const flowField = newFlow.fields.find(
      flowField => flowField.id === field.id
    )
    if (flowField) {
      flowField.options = [...flowField.options, newOption]
      dispatch(updateFlow(flow.id, newFlow))
      setNewOption(null)
    }
  }, [flow, field, newOption, dispatch])

  const performSave = React.useCallback(() => {
    const newFlow = cleanFlow(flow)

    // Update the options in the flow
    newFlow.fields = newFlow.fields.map(flowField => {
      if (flowField.id !== field.id) {
        return flowField
      }

      flowField.options = optionsRef.current
      return flowField
    })

    dispatch(updateFlow(flow.id, newFlow))
  }, [flow, field, dispatch])

  // Delay saving to prevent spamming the API
  const delaySave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      performSave()
    }, 1000)
  }, [performSave])

  // Handler for CustomFieldInput component
  const handleChange = React.useCallback(
    (e: any, index: number) => {
      const setValue = (val: string) => {
        if (index === -1) {
          setNewOption(old => ({ ...old, when: val }))
          return
        }

        setOptions(old => {
          const copy = cloneDeep(old)
          copy[index].when = val
          return copy
        })
        delaySave()
      }

      const val = fieldType === 'boolean' ? e.target.checked : e.target.value
      const prevVal =
        (index === -1 ? newOption.when : options[index].when) ?? ''

      // Store boolean as string representation
      if (fieldType === 'boolean') {
        setValue(`${val}`)
        return
      }

      // Store pre-formatted text
      if (
        [
          'text',
          'multiline',
          'dropdown',
          'date-only',
          'date-time',
          'month-year',
          'user',
          'phone'
        ].includes(fieldType)
      ) {
        setValue(val)
        return
      }

      // Logic to add or remove selections
      if (fieldType === 'multiselect') {
        // Split selections, remove empty strings
        const prev = prevVal.split('|').filter(Boolean)

        let newVal = ''
        if (prev.includes(val)) {
          // Remove selected value case
          newVal = prev.filter(x => x !== val).join('|')
        } else {
          // Add selection case
          newVal = [...prev, val].join('|')
        }

        setValue(newVal)
        return
      }

      console.warn(`Unknown field type ${fieldType}`)
    },
    [fieldType, newOption, options, delaySave]
  )

  const handleValueChange = React.useCallback(
    (key: 'when' | 'then', value: any, index: number) => {
      // Handle when for new and existing options
      if (key === 'when') {
        handleChange(value, index)
        return
      }

      // Handle new option then
      if (index === -1) {
        setNewOption(old => ({ ...old, then: value }))
        return
      }

      // Handle existing option then
      setOptions(old => {
        const copy = cloneDeep(old)
        copy[index].then = value
        return copy
      })
      delaySave()
    },
    [handleChange, delaySave]
  )

  const handleDelete = React.useCallback(
    (index: number) => {
      setOptions(old => {
        const copy = cloneDeep(old)
        copy.splice(index, 1)
        return copy
      })
      delaySave()
    },
    [delaySave]
  )

  // Auto save new transition when it is valid
  React.useEffect(() => {
    if (newOption !== null && canConfirm) {
      confirmNewOption()
    }
  }, [newOption, canConfirm, confirmNewOption])

  if (options.length === 0 && newOption === null) {
    return (
      <div className={classes.emptyRoot}>
        <Button
          onClick={() => addNewOption()}
          className={clsx(classes.button, classes.padLeft)}
        >
          Add transition
        </Button>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={classes.header}>When field has this value</div>

        <div className={classes.header}>Transition to this flow stage</div>

        <div />

        {options.map((opt, i) => (
          <FlowFieldOption
            key={i}
            flow={flow}
            field={field}
            option={opt}
            onChange={handleValueChange}
            tab={tab}
            index={i}
          >
            <Trash
              onClick={() => handleDelete(i)}
              size={theme.size.base}
              className={classes.clickable}
            />
          </FlowFieldOption>
        ))}

        {newOption !== null && (
          <FlowFieldOption
            flow={flow}
            field={field}
            option={newOption}
            onChange={handleValueChange}
            tab={tab}
          >
            <CloseCircle
              onClick={() => setNewOption(null)}
              size={theme.size.base}
              className={classes.clickable}
            />
          </FlowFieldOption>
        )}
      </div>

      {newOption === null && (
        <Button
          onClick={() => addNewOption()}
          className={clsx(classes.button, classes.padTop, classes.padLeft)}
        >
          Add transition
        </Button>
      )}
    </div>
  )
}

export default FlowFieldOptions
